import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Header = ({ onContactClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };
  
  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-black/90 backdrop-blur-xl shadow-2xl border-b border-brand-olive/20' 
          : 'bg-transparent backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_50a99d4b-16e5-4a25-a3c2-534efc6745c6/artifacts/9ur1riyu_d51c164a-21d7-4291-b45e-5c3d3e3a1260-1_all_5079.jpg" 
              alt="JRT Chideme Media" 
              className="w-12 h-12 rounded-lg"
            />
            <div>
              <div className="font-bold text-white text-lg">JRT Chideme Media</div>
              <div className="text-xs text-brand-cream/70">Digital Marketing Agency</div>
            </div>
          </div>
          
          {/* Desktop navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="text-white hover:text-brand-maroon font-medium transition-colors duration-200"
            >
              Home
            </button>
            
            {/* Packages Dropdown */}
            <div className="relative group">
              <button 
                onClick={() => scrollToSection('packages')}
                className="text-white hover:text-brand-maroon font-medium transition-colors duration-200 flex items-center gap-1"
              >
                Packages
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Dropdown Menu */}
              <div className="absolute top-full left-0 mt-2 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-2">
                <div className="bg-black/95 backdrop-blur-xl border border-brand-olive/30 rounded-xl shadow-2xl py-2">
                  <a href="/packages/starter" className="block px-4 py-3 text-brand-cream/90 hover:bg-brand-olive/20 hover:text-brand-maroon transition-colors duration-200">
                    <div className="font-semibold">Starter - £99/mo</div>
                    <div className="text-xs text-brand-cream/60">For new businesses</div>
                  </a>
                  <a href="/packages/basic" className="block px-4 py-3 text-brand-cream/90 hover:bg-brand-olive/20 hover:text-brand-maroon transition-colors duration-200">
                    <div className="font-semibold flex items-center gap-2">
                      Basic - £150/mo
                      <span className="text-xs bg-brand-maroon text-white px-2 py-0.5 rounded-full">Popular</span>
                    </div>
                    <div className="text-xs text-brand-cream/60">For growing freelancers</div>
                  </a>
                  <a href="/packages/advanced" className="block px-4 py-3 text-brand-cream/90 hover:bg-brand-olive/20 hover:text-brand-maroon transition-colors duration-200">
                    <div className="font-semibold">Advanced - £255/mo</div>
                    <div className="text-xs text-brand-cream/60">For established clients</div>
                  </a>
                  <a href="/packages/premium" className="block px-4 py-3 text-brand-cream/90 hover:bg-brand-olive/20 hover:text-brand-maroon transition-colors duration-200">
                    <div className="font-semibold">Premium - £450/mo</div>
                    <div className="text-xs text-brand-cream/60">Full-service growth</div>
                  </a>
                </div>
              </div>
            </div>
            
            {/* Services Dropdown */}
            <div className="relative group">
              <button 
                className="text-white hover:text-brand-maroon font-medium transition-colors duration-200 flex items-center gap-1"
              >
                Services
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Dropdown Menu */}
              <div className="absolute top-full left-0 mt-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-2 z-50">
                <div className="bg-black/95 backdrop-blur-xl border border-brand-olive/30 rounded-xl shadow-2xl py-2">
                  <a href="/services/media-strategy" className="block px-4 py-3 text-brand-cream/90 hover:bg-brand-olive/20 hover:text-brand-maroon transition-colors duration-200">
                    <div className="font-semibold">Media Strategy</div>
                    <div className="text-xs text-brand-cream/60">Data-driven media planning</div>
                  </a>
                  <a href="/services/brand-storytelling" className="block px-4 py-3 text-brand-cream/90 hover:bg-brand-olive/20 hover:text-brand-maroon transition-colors duration-200">
                    <div className="font-semibold">Brand Storytelling</div>
                    <div className="text-xs text-brand-cream/60">Compelling brand narratives</div>
                  </a>
                  <a href="/services/media-production" className="block px-4 py-3 text-brand-cream/90 hover:bg-brand-olive/20 hover:text-brand-maroon transition-colors duration-200">
                    <div className="font-semibold">Media Production</div>
                    <div className="text-xs text-brand-cream/60">Video & photography services</div>
                  </a>
                  <a href="/services/press-and-pr" className="block px-4 py-3 text-brand-cream/90 hover:bg-brand-olive/20 hover:text-brand-maroon transition-colors duration-200">
                    <div className="font-semibold">Press & PR</div>
                    <div className="text-xs text-brand-cream/60">Media coverage & thought leadership</div>
                  </a>
                  <a href="/services/digital-content" className="block px-4 py-3 text-brand-cream/90 hover:bg-brand-olive/20 hover:text-brand-maroon transition-colors duration-200">
                    <div className="font-semibold">Digital Content</div>
                    <div className="text-xs text-brand-cream/60">Social, blogs, email & more</div>
                  </a>
                </div>
              </div>
            </div>
            
            <a 
              href="/newsletters"
              className="text-white hover:text-brand-maroon font-medium transition-colors duration-200"
            >
              Success Stories
            </a>
            
            <a 
              href="/about"
              className="text-white hover:text-brand-maroon font-medium transition-colors duration-200"
            >
              About
            </a>
            
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-white hover:text-brand-maroon font-medium transition-colors duration-200"
            >
              Contact
            </button>
            <Button 
              onClick={onContactClick}
              className="bg-brand-maroon hover:bg-brand-maroon/90 text-white font-semibold transition-all duration-300 hover:scale-105 shadow-lg shadow-brand-maroon/50"
            >
              Book Consultation
            </Button>
          </nav>
          
          {/* Mobile menu button */}
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden py-6 border-t border-brand-olive/20 bg-brand-brown">
            <div className="flex flex-col gap-4">
              <button 
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setIsMobileMenuOpen(false);
                }}
                className="text-white hover:text-brand-yellow font-medium transition-colors duration-200 text-left"
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection('packages')}
                className="text-white hover:text-brand-yellow font-medium transition-colors duration-200 text-left"
              >
                Packages
              </button>
              <a 
                href="/newsletters"
                className="text-white hover:text-brand-yellow font-medium transition-colors duration-200 text-left"
              >
                Success Stories
              </a>
              <a 
                href="/about"
                className="text-white hover:text-brand-yellow font-medium transition-colors duration-200 text-left"
              >
                About
              </a>
              <button 
                onClick={() => scrollToSection('contact')}
                className="text-white hover:text-brand-yellow font-medium transition-colors duration-200 text-left"
              >
                Contact
              </button>
              <Button 
                onClick={onContactClick}
                className="bg-brand-yellow hover:bg-brand-yellow/90 text-brand-brown font-semibold w-full"
              >
                Book Consultation
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
